import { isResolvedAck } from '../lib/findingAck';
import React, { useState } from 'react';
import { reportForPresentation } from '../lib/scoring';
import { t } from '../lib/i18n';
import Icon from '../components/Icon';
import { getActionText } from './issueText';

/**
 * ActionSummary — Clear checklist of actions for the accountant.
 * Shows critical items (must fix) and warnings (should check) with
 * click-to-scroll and copy-to-clipboard functionality.
 *
 * Extracted from index.html (previously inline ~lines 965-1067).
 *
 * @param {{ report: { issues: Array }, systemName: string }} props
 */
export default function ActionSummary({ report: storedReport, systemName, getAck }) {
    const report = reportForPresentation(storedReport);
    const [copied, setCopied] = useState(false);
    const issues = (report?.issues || []).filter(i => !['financial_result', 'scoring', 'health'].includes(i.specialType));
    const critical = issues.filter(i => i.type === 'error' && !isResolvedAck(getAck?.(i.ruleId, i)));
    const check = issues.filter(i => (i.type === 'warning' || i.specialType === 'review_signal') && !isResolvedAck(getAck?.(i.ruleId, i)));

    if (critical.length === 0 && check.length === 0 && report?.presentationRetiredRules?.length) return (
        <div className="mb-8 p-6 rounded-2xl border border-slate-300 bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300" data-history-notice="true">
            <p>{t('audit_history_notice')}</p>
        </div>
    );
    if (critical.length === 0 && check.length === 0) {
        return (
            <div className="mb-8 p-6 rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20">
                <div className="flex items-center gap-3 mb-2">
                    <Icon name="clipboard-check" className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    <h2 className="text-xl font-bold font-heading text-emerald-800 dark:text-emerald-200">{t('summary_title')}</h2>
                </div>
                <p className="text-emerald-700 dark:text-emerald-300 font-medium">{t('summary_ok')}</p>
            </div>
        );
    }

    // Build text for clipboard copy
    const buildActionText = (issue, idx) => {
        const action = getActionText(issue);
        const accounts = (issue.details || [])
            .filter(d => d.accNum || d.id)
            .map(d => d.accNum || d.id)
            .slice(0, 5)
            .join(', ');
        return `${idx + 1}. ${action}${accounts ? ` [${accounts}]` : ''}`;
    };

    const copyText = () => {
        const lines = [];
        lines.push(`${t('summary_title')} \u2014 ${systemName || ''} ${new Date().toLocaleDateString()}`);
        lines.push(`${'─'.repeat(50)}`);
        if (critical.length > 0) {
            lines.push(`\n\uD83D\uDD34 ${t('summary_fix')}`);
            critical.forEach((issue, i) => lines.push(buildActionText(issue, i)));
        }
        if (check.length > 0) {
            lines.push(`\n\uD83D\uDFE0 ${t('summary_check')}`);
            check.forEach((issue, i) => lines.push(buildActionText(issue, i)));
        }
        try {
            navigator.clipboard.writeText(lines.join('\n')).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }).catch(() => { setCopied(false); });
        } catch (e) { /* clipboard not available */ }
    };

    const scrollToIssue = (issue, idx) => {
        const el = document.querySelector(`[data-issue-id="${issue.ruleId || idx}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const toggle = el.querySelector('[data-expand-toggle]');
            if (toggle && el.getAttribute('data-expanded') === 'false') toggle.click();
        }
    };

    return (
        <div className="mb-8 p-6 rounded-2xl border-2 border-copper-200 dark:border-copper-800 bg-white dark:bg-slate-800 shadow-lg">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-copper-100 dark:bg-copper-900/50 flex items-center justify-center">
                        <Icon name="clipboard-check" className="w-5 h-5 text-copper-600 dark:text-copper-400" />
                    </div>
                    <h2 className="text-xl font-bold font-heading dark:text-white">{t('summary_title')}</h2>
                </div>
                <button onClick={copyText} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-copper-50 dark:bg-copper-900/30 text-copper-700 dark:text-copper-300 border border-copper-200 dark:border-copper-700 rounded-lg hover:bg-copper-100 dark:hover:bg-copper-900/50 transition-colors">
                    <Icon name={copied ? "check" : "copy"} className="w-3.5 h-3.5" />
                    {copied ? t('summary_copied') : t('summary_copy')}
                </button>
            </div>

            {/* CRITICAL — must fix */}
            {critical.length > 0 && (
                <div className="mb-4">
                    <h3 className="text-sm font-bold text-red-700 dark:text-red-400 mb-2 flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span> {t('summary_fix')}
                    </h3>
                    <ol className="space-y-1.5 pl-4">
                        {critical.map((issue, idx) => (
                            <li key={'sc' + idx} className="text-sm text-slate-800 dark:text-slate-100 flex gap-2 cursor-pointer hover:text-red-600 dark:hover:text-red-300 transition-colors"
                                onClick={() => scrollToIssue(issue, idx)}>
                                <span className="font-mono text-xs text-red-400 dark:text-red-500 font-bold mt-0.5 flex-shrink-0">{idx + 1}.</span>
                                <span className="hover:underline">{getActionText(issue)}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* CHECK — should review */}
            {check.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block"></span> {t('summary_check')}
                    </h3>
                    <ol className="space-y-1.5 pl-4">
                        {check.map((issue, idx) => (
                            <li key={'sw' + idx} className="text-sm text-slate-800 dark:text-slate-100 flex gap-2 cursor-pointer hover:text-amber-600 dark:hover:text-amber-300 transition-colors"
                                onClick={() => scrollToIssue(issue, idx)}>
                                <span className="font-mono text-xs text-amber-400 dark:text-amber-500 font-bold mt-0.5 flex-shrink-0">{idx + 1}.</span>
                                <span className="hover:underline">{getActionText(issue)}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
